import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, MapPin, GraduationCap, Building2 } from "lucide-react";

export default function UniversityDetail() {
  const { school } = useParams();
  const [state, setState] = useState(null);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`/bschools`).then(r => r.data.bschools.find(b => b.short === school)),
      api.get(`/bschools/${encodeURIComponent(school)}/partners`),
    ]).then(([m, p]) => { setMeta(m); setState(p.data); });
  }, [school]);

  if (!state) return (
    <div className="max-w-4xl mx-auto px-6 py-16">
      <div className="moe-shimmer h-8 w-64 mb-4"/>
      <div className="moe-shimmer h-4 w-96 mb-2"/>
    </div>
  );

  const countries = Object.keys(state.grouped).sort();

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-24">
      <Link to="/universities" className="inline-flex items-center gap-1 text-sm text-muted hover:text-elec">
        <ArrowLeft size={14}/> All schools
      </Link>

      <div className="mt-6">
        {meta && <div className="text-xs font-bold text-elec">RANK #{meta.rank}</div>}
        <h1 className="mt-2 text-4xl sm:text-5xl font-bold tracking-tight">{meta?.short || school}</h1>
        <div className="mt-2 text-lg text-muted">{meta?.name}</div>
        <div className="mt-1 flex items-center gap-1 text-sm text-ink"><MapPin size={13} className="text-elec"/>{meta?.city}</div>
      </div>

      <div className="mt-10 grid md:grid-cols-3 gap-3">
        <div className="moe-card p-5">
          <div className="text-xs uppercase tracking-widest text-muted font-semibold">Total European partners</div>
          <div className="mt-2 text-4xl font-bold">{state.count}</div>
        </div>
        <div className="moe-card p-5">
          <div className="text-xs uppercase tracking-widest text-muted font-semibold">Countries covered</div>
          <div className="mt-2 text-4xl font-bold">{countries.length}</div>
        </div>
        <div className="moe-card p-5">
          <div className="text-xs uppercase tracking-widest text-muted font-semibold">Top destination</div>
          <div className="mt-2 text-xl font-bold">{countries.map(c=>[c, state.grouped[c].length]).sort((a,b)=>b[1]-a[1])[0]?.[0] || "–"}</div>
        </div>
      </div>

      <div className="mt-12 space-y-8">
        {countries.map(country => (
          <div key={country}>
            <div className="flex items-baseline justify-between border-b border-ink pb-3 mb-4">
              <h2 className="text-2xl font-bold">{country}</h2>
              <Link to={`/countries/${encodeURIComponent(country)}`} className="text-sm text-elec font-semibold hover:underline">
                {country} visa dossier →
              </Link>
            </div>
            <div className="grid md:grid-cols-2 gap-3 moe-stagger">
              {state.grouped[country].map(p => (
                <div key={p.id} className="moe-card p-4 moe-hover-lift">
                  <div className="flex items-start gap-2">
                    <GraduationCap size={16} className="text-elec mt-0.5" strokeWidth={1.8}/>
                    <div>
                      <div className="text-sm font-bold text-ink leading-tight">{p.partner_name}</div>
                      <div className="text-xs text-muted mt-0.5">{p.partner_city}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {countries.length === 0 && (
        <div className="mt-12 moe-card p-10 text-center">
          <Building2 size={32} className="mx-auto text-muted moe-float"/>
          <div className="mt-4 text-lg font-bold">No European partners on record</div>
          <div className="mt-2 text-sm text-muted">This school may have partnerships not yet in our dataset.</div>
        </div>
      )}
    </div>
  );
}
